import { observer } from 'mobx-react-lite'
import * as THREE from 'three'
import { useMemo } from 'react'
import { useRunState } from '../../provider/useRunState'
import { store } from '@/src/stores/store'
import type { TrackController } from '../track/trackController'
import type { ObstacleCollisionData } from './obstacleCollision'

type ObstacleHitboxDebugProps = {
    collisionData: ObstacleCollisionData
    trackController: TrackController
}

export const ObstacleHitboxDebug = observer(function ObstacleHitboxDebug({
    collisionData,
    trackController,
}: ObstacleHitboxDebugProps) {
    const runState = useRunState()
    const transforms = useMemo(
        () =>
            collisionData.obstacles.map((obstacle) => {
                const frame = trackController.frameAtPosition(obstacle.trackPosition)
                const position = frame.position
                    .clone()
                    .add(new THREE.Vector3(obstacle.laneX, obstacle.centerY, 0).applyQuaternion(frame.cameraQuaternion))

                return {
                    id: `obstacle-path-${obstacle.index}`,
                    position: position.toArray() as [number, number, number],
                    quaternion: frame.cameraQuaternion.toArray() as [number, number, number, number],
                    size: [
                        obstacle.hitbox.x * 2,
                        obstacle.hitbox.y * 2,
                        obstacle.hitbox.z * 2,
                    ] as [number, number, number],
                }
            }),
        [collisionData, trackController],
    )

    if (!store.debugShowObstacleHitboxes) return null

    return (
        <group>
            {transforms.map((transform, index) => (
                <mesh
                    key={`${transform.id}-${index}`}
                    position={transform.position}
                    quaternion={transform.quaternion}
                >
                    <boxGeometry args={transform.size} />
                    <meshBasicMaterial
                        color={
                            runState.snapshot.debugCollisionObstacleId === transform.id
                                ? '#ff4d4f'
                                : '#54f38d'
                        }
                        wireframe
                        transparent
                        opacity={0.9}
                        depthTest={false}
                    />
                </mesh>
            ))}
        </group>
    )
})
